import type { ApiError } from '@crv/contracts';
import {
  BootstrapResponseSchema,
  IdentityTokensCompatibilityResponseSchema,
  InteractiveTokensCompatibilityResponseSchema,
  LoginRequestSchema,
  RefreshTokenRequestSchema,
  SelectCompanyRequestSchema,
  SelectIdentityRequestSchema,
  SignupRequestSchema,
  SignupResponseSchema,
} from '@crv/contracts/auth';
import type {
  BootstrapResponse,
  InteractiveTokensCompatibilityResponse,
  LoginRequest,
  RefreshTokenRequest,
  SelectCompanyRequest,
  SelectIdentityRequest,
  SignupRequest,
  SignupResponse,
} from '@crv/contracts/auth';
import { z } from 'zod';

interface FetchLikeResponse {
  ok: boolean;
  status: number;
  headers: { get(name: string): string | null };
  json(): Promise<unknown>;
  text(): Promise<string>;
}

interface FetchLikeInit {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}

type FetchLike = (input: string, init?: FetchLikeInit) => Promise<FetchLikeResponse>;

export interface AuthClientConfig {
  baseUrl: string;
  fetchImpl?: FetchLike;
}

export interface AuthClient {
  signup(input: SignupRequest): Promise<SignupResponse>;
  login(input: LoginRequest): Promise<InteractiveTokensCompatibilityResponse>;
  refreshToken(input: RefreshTokenRequest): Promise<InteractiveTokensCompatibilityResponse>;
  selectCompany(accessToken: string, input: SelectCompanyRequest): Promise<InteractiveTokensCompatibilityResponse>;
  selectIdentity(accessToken: string, input?: SelectIdentityRequest): Promise<InteractiveTokensCompatibilityResponse>;
  bootstrap(accessToken: string): Promise<BootstrapResponse>;
}

export class AuthClientError extends Error {
  readonly status: number;
  readonly apiError?: ApiError;

  constructor(message: string, status: number, apiError?: ApiError) {
    super(message);
    this.name = 'AuthClientError';
    this.status = status;
    this.apiError = apiError;
  }
}

const apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  traceId: z.string().optional(),
  details: z.unknown().optional(),
});

export function createAuthClient(config: AuthClientConfig): AuthClient {
  const baseUrl = normalizeBaseUrl(config.baseUrl);
  const fetchImpl =
    config.fetchImpl ?? ((globalThis as { fetch?: FetchLike }).fetch ?? undefined);
  if (!fetchImpl) {
    throw new Error('No fetch implementation available for AuthClient');
  }

  return {
    async signup(input): Promise<SignupResponse> {
      const payload = SignupRequestSchema.parse(input);
      return request(`${baseUrl}/signup`, SignupResponseSchema, fetchImpl, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    async login(input): Promise<InteractiveTokensCompatibilityResponse> {
      const payload = LoginRequestSchema.parse(input);
      return request(`${baseUrl}/login`, IdentityTokensCompatibilityResponseSchema, fetchImpl, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    async refreshToken(input): Promise<InteractiveTokensCompatibilityResponse> {
      const payload = RefreshTokenRequestSchema.parse(input);
      return request(`${baseUrl}/refresh`, InteractiveTokensCompatibilityResponseSchema, fetchImpl, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    async selectCompany(accessToken: string, input: SelectCompanyRequest): Promise<InteractiveTokensCompatibilityResponse> {
      const payload = SelectCompanyRequestSchema.parse(input);
      return request(
        `${baseUrl}/select-company`,
        InteractiveTokensCompatibilityResponseSchema.refine(
          (value) => value.sessionType === 'company' && value.companyId === payload.companyId,
          { message: 'Token response does not match the selected company' },
        ),
        fetchImpl,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        },
      );
    },
    async selectIdentity(
      accessToken: string,
      input: SelectIdentityRequest = {},
    ): Promise<InteractiveTokensCompatibilityResponse> {
      const payload = SelectIdentityRequestSchema.parse(input);
      return request(
        `${baseUrl}/select-identity`,
        IdentityTokensCompatibilityResponseSchema,
        fetchImpl,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        },
      );
    },
    async bootstrap(accessToken: string): Promise<BootstrapResponse> {
      return request(
        `${baseUrl}/bootstrap`,
        BootstrapResponseSchema,
        fetchImpl,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
    },
  };
}

async function request<T>(
  url: string,
  schema: z.ZodType<T>,
  fetchImpl: FetchLike,
  init: FetchLikeInit,
): Promise<T> {
  const response = await fetchImpl(url, {
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });

  const body = await parseResponseBody(response);

  if (!response.ok) {
    const parsedApiError = apiErrorSchema.safeParse(body);
    const message =
      parsedApiError.success && parsedApiError.data.message
        ? parsedApiError.data.message
        : `Auth request failed with status ${String(response.status)}`;
    throw new AuthClientError(
      message,
      response.status,
      parsedApiError.success ? (parsedApiError.data as ApiError) : undefined,
    );
  }

  return schema.parse(body);
}

async function parseResponseBody(response: FetchLikeResponse): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return response.text();
  }

  try {
    return await response.json();
  } catch {
    throw new AuthClientError('Invalid JSON response from auth service', response.status);
  }
}

function normalizeBaseUrl(raw: string): string {
  if (raw.endsWith('/')) {
    return raw.slice(0, -1);
  }

  return raw;
}
